import { useDataTable } from '@abyss/web/hooks/useDataTable';
import { useDataTableFilterColumnTypeMapper } from '../GridFilterColumnType';
import { useDataTableColumnMapper } from '../DataTableColumnMapper/useDataTableColumnMapper';
import { useGridDefaults } from '../GridDefault';
import { useUserStatePersistence } from '../UserStatePersistence';
import type { UseGridBuilderOptions } from './UseGridBuilderOptions';
import type { UseGridBuilderResult } from './UseGridBuilderResult';
/**
 * High-level orchestrator hook that composes the individual GridBuilder
 * hooks into a single, easy-to-consume call.
 *
 * **Internally, the following hooks are called in order:**
 *
 * 1. `useDataTableFilterColumnTypeMapper` — builds the
 *    `filterColumnTypes` map from column definitions.
 * 2. `useDataTableLinkColumnMapper` — returns a new column array with
 *    `Cell` renderers attached for every `linkColumn: true` column.
 * 3. `useDataTable` — creates the DataTable state with the mapped
 *    columns and filter types.
 * 4. `useGridDefaults` — applies standard configuration overrides to
 *    the live `tableState`.
 * 5. `useUserStatePersistence` — persists and restores grid + form
 *    state to/from `localStorage`.
 *
 * The hook accepts the `useForm` instance, column definitions,
 * `onRefresh` callback, DataTable passthrough options, and persistence
 * config — then returns the fully-wired `tableState` along with
 * `clearPreference`, `resetFormValues`, and `onRefresh`.
 *
 * @example
 * ```tsx
 * const form = useForm({ defaultValues: { name: '' } });
 *
 * const { tableState, clearPreference, resetFormValues, onRefresh } =
 *   useGridBuilder({
 *     columns,
 *     form,
 *     onRefresh: () => Action.Refresh(),
 *     dataTableOptions: {
 *       showPagination: true,
 *       pageSizeOptions: [5, 10, 15],
 *       showDownloadButton: true,
 *       showGlobalFilter: true,
 *       showFilterDataset: true,
 *       showTableSettings: true,
 *       showColumnVisibilityConfig: true,
 *     },
 *     persistence: {
 *       enabled: true,
 *       clientId: 5,
 *       storageName: 'my-grid',
 *     },
 *   });
 *
 * <DataTable tableState={tableState} />
 * ```
 */
export function useGridBuilder(options: UseGridBuilderOptions): UseGridBuilderResult {
  const {
    columns,
    form,
    onRefresh,
    dataTableOptions = {},
    persistence,
  } = options;

  // 1. Build the filterColumnTypes map from column definitions.
  const filterColumnTypes = useDataTableFilterColumnTypeMapper({ columns });

  // 2. Build Cell renderers for any columns marked as linkColumn.
  //    Returns a new array — original column objects are not mutated.
  const finalColumns = useDataTableColumnMapper({ columns });

  // 3. Create the DataTable state with mapped columns and filter types.
  const tableState = useDataTable({
    initialColumns: finalColumns,
    filterColumnTypes: filterColumnTypes as Record<string, any>,
    ...dataTableOptions,
  });

  // 4. Apply default overrides to the live table state.
  useGridDefaults({ tableState });

  // 5. Persist and restore grid + form state (opt-in via `persistence`).
  const { clearPreference, resetFormValues } = useUserStatePersistence({
    enabled: persistence?.enabled ?? false,
    clientId: persistence?.clientId ?? null,
    storageName: persistence?.storageName ?? '',
    debug: persistence?.debug,
    tableState,
    form,
    onClearPreference: () => {
      onRefresh();
    },
    onRestoreState: () => {
      onRefresh();
    },
    onNoSavedState: () => {
      onRefresh();
    },
  });

  return {
    tableState,
    clearPreference,
    resetFormValues,
    onRefresh,
  };
}
